import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { deleteUser, getUser } from "../../utils/client";
import TopNavBar from "../../components/TopNavBar";
import { useAlert } from "../../components/AlertProvider";
import { GetUserOutput, ValidationError } from "baptize-america-client";
import { NavLink } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import ChurchInfoItem from "../../components/ChurchInfoItem";
import { getRole, isAdmin } from "../../utils/helper";
import { LoginWindow } from "../../components/LoginWindow";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useUserData } from "../../components/DataProvider";

const DeleteUser = () => {
  const { authStatus } = useAuthenticator((context) => [context.user]);
  const { attributes } = useUserData();
  const theme = useTheme();
  const { setAlert } = useAlert();
  const [email, setEmail] = useState("");
  const [userData, setUserData] = useState<GetUserOutput | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

  const styles = {
    navBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexDirection: "column",
      mb: 5,
    },
    actionButton: {
      height: 60,
      width: 300,
      backgroundColor: "#00169E",
      color: "#FFF",
      fontSize: 16,
    },
  };

  const handleFetchUser = async () => {
    setLoading(true);
    try {
      const user = await getUser({ email });
      setUserData(user ?? null);
    } catch (err) {
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error finding user",
        alertType: "error",
      });
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userData?.sub) return;
    setLoading(true);
    try {
      await deleteUser({ sub: userData.sub });
      setUserData(null);
      setEmail("");
      setAlert({
        showAlert: true,
        alertMsg: "User removed",
        alertType: "success",
      });
    } catch (err) {
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error deleting user",
        alertType: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setLoading(false);
    }
  };

  return authStatus === "authenticated" && isAdmin(attributes) ? (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box
        sx={[
          {
            "& .MuiTextField-root": {
              m: 1,
              width: theme.breakpoints.values.sm,
            },
          },
          styles.navBar,
        ]}
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          handleFetchUser();
        }}
      >
        <Box sx={{ mb: 4 }}>
          <TopNavBar showBackArrow={true} />
        </Box>
        <TextField
          fullWidth
          autoComplete="off"
          label="User Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
        />
        <Button
          variant="contained"
          fullWidth
          onClick={handleFetchUser}
          disabled={loading}
          style={styles.actionButton}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Lookup User"
          )}
        </Button>

        {userData && (
          <Box
            sx={{ p: 2, mt: "3rem", border: "1px solid #ccc", borderRadius: 2 }}
          >
            <Typography variant="h6">User Info</Typography>
            <ChurchInfoItem label={"Email: "} value={userData.email} />
            <ChurchInfoItem label={"First Name: "} value={userData.firstname} />
            <ChurchInfoItem label={"Last Name: "} value={userData.lastname} />
            <ChurchInfoItem
              label={"Role: "}
              value={getRole(userData.churchId)}
            />
            {userData.churchId?.startsWith("C-") && (
              <ChurchInfoItem
                label={"Associated Church: "}
                // @ts-ignore
                value={
                  <NavLink to={`/churches/${userData.churchId}`}>
                    {userData.churchId}
                  </NavLink>
                }
              />
            )}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                style={{
                  ...styles.actionButton,
                  color: "#fff",
                  backgroundColor: "#9e0000",
                }}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete User
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteUser}
        title={`Delete ${userData?.firstname} ${userData?.lastname}?`}
        content={`Email: ${userData?.email}. This user's role is: **${getRole(userData?.churchId)}!** Continue?`}
      />
    </Container>
  ) : (
    <LoginWindow />
  );
};

export default DeleteUser;
